"use client"

import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { Check } from 'lucide-react'

import { cn } from "./lib/utils"

// Radix's onCheckedChange fires with CheckedState = boolean | "indeterminate".
// Legacy ported pages routinely bind it to a `Dispatch<SetStateAction<boolean>>`
// setter, which fails strict compilation because "indeterminate" isn't a
// boolean. Widen the prop to accept either shape and normalize internally so
// setters that expect a plain boolean still get one.
type CheckedState = boolean | "indeterminate"
// Union covers the three shapes ported call sites throw at us:
//   - Radix native:      (checked: CheckedState) => void
//   - useState setter:   Dispatch<SetStateAction<boolean>>
//   - narrowed handler:  (checked: boolean) => void
// All three receive the same normalized boolean at runtime (see body below).
type CheckedChangeHandler =
  | ((checked: CheckedState) => void)
  | ((checked: boolean) => void)
  | React.Dispatch<React.SetStateAction<boolean>>

type RadixCheckboxProps = React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
export type CheckboxProps = Omit<RadixCheckboxProps, "onCheckedChange"> & {
  onCheckedChange?: CheckedChangeHandler
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, onCheckedChange, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      // P1 (V5 pg22 module-setup): `bg-background` gives the unchecked
      // state a solid ground on top of table stripes / hover fills, so
      // the checkbox stops disappearing on tinted rows. Border stays
      // primary and thickens to 2px so it reads from further away.
      "peer h-4 w-4 shrink-0 rounded-sm border-2 border-primary bg-background ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
      className
    )}
    onCheckedChange={
      onCheckedChange
        ? (checked: CheckedState) =>
            (onCheckedChange as (checked: CheckedState | boolean) => void)(
              checked === "indeterminate" ? false : checked,
            )
        : undefined
    }
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn("flex items-center justify-center text-current")}
    >
      <Check className="h-4 w-4" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox }
